import { AfterContentInit, AfterViewInit, Component, DestroyRef, HostListener, inject, OnInit, ViewChild } from '@angular/core';
import { Ids } from '../../../../models/common/ids.model';
import { PhotographerStatus } from '../../../../models/common/photographer-status.model';

import { Photographer } from '../../../../models/common/photographer.models';
import { SidebarTypeEnum } from '../../../../models/common/sidebar-type.enum';
import { TopbarService } from '../../../../shared/services/topbar.service';
import { UtilityFunctions } from '../../../../shared/utilities/functions.models';
import { FilterService } from '../../../../shared/services/filter.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { OrdersListSchema } from '../../../../models/common/orders-list-schema.model';
import { OrderListItem } from '../../../../models/common/order-list-item.model';
import { ActivatedRoute, Router } from '@angular/router';
import { map } from 'rxjs';
import { PhotographerStatusFilterService } from 'src/app/shared/services/filters/photographer-status-filter.service';
import { MatDialog } from '@angular/material/dialog';
import { FilterDialogData } from '../../../../shared/models/types/filter-dialog-data.type';
import { FilterListComponent } from '../../../../shared/components/filter-list/filter-list.component';
import { AppService } from '../../../../app.service';
import { WrapperResultGraphQL } from '../../../../models/shared/types/wrapper-result-graphql.type';
import { PhotographerQueries } from '../../../../models/shared/enums/get-query';
import { GraphQLService } from '../../../../services/graph-ql.service';
import { PhotographerStatusEnum } from 'src/app/models/common/photographer-status.enum';
import { MutationQuery } from 'src/app/models/shared/enums/mutation-query.enum';
import { ConfirmModalComponent } from 'src/app/shared/components/confirm-modal/confirm-modal.component';

@Component({
	standalone: false,
	selector: 'bt-photographers',
	templateUrl: './photographers.component.html',
	styleUrls: ['./photographers.component.scss']
})
export class PhotographersComponent implements AfterViewInit, AfterContentInit {
	@ViewChild('modalContent') modalContent: any;

	readonly #destroyRef = inject(DestroyRef);
	readonly #filterService = inject(FilterService);
	readonly #appService = inject(AppService);
	readonly #graphQLService = inject(GraphQLService);
	readonly #router = inject(Router);
	readonly #dialog = inject(MatDialog);
	readonly #activatedRoute = inject(ActivatedRoute);

	showModal = false;

	filtersList = [{ id: 'photographerStatus', label: 'Photographer Status' }];

	photographerStatus: FilterDialogData<PhotographerStatus> = {
		title: 'Photographer Status',
		selected: [],
		filterService: inject(PhotographerStatusFilterService)
	};

	chips: Array<any> = [];
	currentSidebar: SidebarTypeEnum;
	sidebarType = SidebarTypeEnum;

	tmpPhotog: Photographer;
	photographers: { data?: Array<Photographer>; paginationPage?: number; paginationSize: number; searchText?: string; areFiltered?: boolean } = {
		searchText: '',
		paginationSize: 20
	};
	photographersLoaderId = 'photographersLoaderId';

	photographerOrders: OrdersListSchema<OrderListItem> = { orders: [], total: 0 };

	constructor(private topbarService: TopbarService) {
		this.searchTextSubscription();
		this.queryParamsSubscribe();
	}

	ngAfterViewInit(): void {}

	ngAfterContentInit(): void {
		//Acquisizione dei parametri dell'url
		this.getData();
	}

	clearFilter(event: any, filter: string): void {
		event.stopPropagation();
		this[filter].selected = [];
		(this[filter] as FilterDialogData<any>).filterService.clearSelection();
		this.#filterService.removeDataFromUrl(filter, () => null);
	}

	getData() {
		const params = this.#filterService.getDataFromUrl();

		if (params['search']) {
			this.photographers.searchText = params['search'];
			this.chips.unshift({ search: params['search'] });
		}

		if (params['photographerStatus']) {
			const selectedStatus = params['photographerStatus'].split('-').map((el) => +el);
			this.#graphQLService
				.getData<PhotographerStatus>(PhotographerQueries.GET_PHOTOGRAPHER_STATUS, { statusIds: selectedStatus })
				.pipe(
					takeUntilDestroyed(this.#destroyRef),
					map((res: any) => res['data']['photographerStatus'])
				)
				.subscribe({
					next: (data) => {
						this.photographerStatus.selected = data.nodes.map((el) => ({
							...el,
							disabled: false,
							selected: true,
							displayId: el.id,
							displayName: el.name
						}));
					}
				});
		}
	}

	applyFilters(filter: string, selected: Array<any>) {
		if (!selected.length) {
			// Rimozione il parametro dall'url
			this.#filterService.removeDataFromUrl(filter, () => null);
		} else {
			this[filter].selected = selected;
			const ids = selected.map((el) => `${el.displayId}`).join('-');
			this.#filterService.setQueryParams({ [filter]: ids });
		}
	}

	onItemAction(item: Photographer, sidebarType: SidebarTypeEnum) {
		item.showMenu = false;
		this.currentSidebar = sidebarType;
		this.tmpPhotog = UtilityFunctions.DeepClone(item);
	}

	openDeletePhotographerDialog(photographer: Photographer): void {
		this.#dialog
			.open(ConfirmModalComponent, {
				data: {
					title: 'Delete Photographer',
					message: `Are you sure you want to delete ${photographer.fullName} photographer?`
				},
				height: 'auto',
				width: 'auto',
				minWidth: '25%',
				maxHeight: '25%',
				position: {
					top: '0',
					left: '37.5%'
				}
			})
			.afterClosed()
			.subscribe({
				next: (res) => {
					if (res) {
						this.deletePhotographer(photographer);
					}
				}
			});
	}

	createCustomOrder(item: Photographer) {
		this.#router.navigate(['/company/new-order'], { queryParams: { photographerId: item.personId } });
	}

	editPhotographer(item: Photographer) {
		this.#router.navigate([`/company/edit-photographer/${item.personId}`]);
	}

	private getPhotographers(page: number, params?: { [key: string]: string }) {
		this.photographers.paginationPage = page;
		const filters = {
			companyId: this.#appService.companyId,
			paginationPage: page,
			paginationSize: this.photographers.paginationSize,
			search: '',
			statusIds:
				params && params['photographerStatus']
					? params['photographerStatus'].split('-').map((el) => new Ids(+el))
					: this.photographerStatus.selected.map((el) => new Ids(el.displayId)),
			photographerIds: []
		};

		this.#graphQLService
			.getData<Photographer>(PhotographerQueries.GET_PHOTOGRAPHERS, filters)
			.pipe(
				takeUntilDestroyed(this.#destroyRef),
				map((res: any) => res['data']['photographers'])
			)
			.subscribe((data: WrapperResultGraphQL<Photographer>) => {
				this.photographers.data = data.nodes.map((el) => ({ ...el }));
			});
	}

	parseDate(str: string): Date {
		const [datePart, timePart] = str.split(' ');
		const [day, month, year] = datePart.split('/').map(Number);
		const [hours, minutes, seconds] = timePart.split(':').map(Number);
		return new Date(year, month - 1, day, hours, minutes, seconds);
	}

	inViewportAction(ev: any, item: Photographer) {
		item.isVisible = ev.isVisible;
		if (item.isVisible) {
			const index = this.photographers.data.findIndex((el) => el.personId === item.personId);
			if (
				index === this.photographers.data.length - 1 &&
				this.photographers.data.length / this.photographers.paginationSize === this.photographers.paginationPage + 1
			) {
				this.getPhotographers(this.photographers.paginationPage + 1);
			}
		}
	}

	savedInfo() {
		this.tmpPhotog = null;
		this.currentSidebar = null;
		this.getPhotographers(0);
	}

	deletePhotographer(photographer: Photographer) {
		const updatePhotographer = {
			photographerId: photographer.personId,
			discountPercentage: null,
			discountThreshold: null,
			endDate: photographer.hiredUntil,
			startDate: photographer.hiredFrom,
			maxItems: null,
			shotCost: null,
			statusId: PhotographerStatusEnum.Deleted,
			automaticRenewal: false,
			brandIdCategoryId: [],
			currentUserId: this.#appService.user.userId,
			companyId: this.#appService.companyId
		};

		this.#graphQLService
			.postData(MutationQuery.EDIT_PHOTOGRAPHER, updatePhotographer)
			.pipe(takeUntilDestroyed(this.#destroyRef))
			.subscribe({
				next: () => {
					const index = this.photographers.data.findIndex((p) => p.personId === photographer.personId);
					if (index >= 0) this.photographers.data.splice(index, 1);
				}
			});
	}

	openDialogFilter(title: string = ''): void {
		const initialValue = [...this[title].selected];
		const dialog = this.#dialog.open(FilterListComponent<PhotographerStatus>, {
			data: { title: this[title].title, selected: this[title].selected, filterService: this[title].filterService, variables: this[title].variables },
			height: '70%',
			width: 'auto',
			minWidth: '25%'
		});

		dialog.afterClosed().subscribe({
			next: (res) => {
				if (res) {
					this.applyFilters(title, res);
					this[title].selected = initialValue;
				}
			}
		});
	}

	getLabel(filter: string): string {
		return this[filter].selected.length
			? `: ${this[filter].selected
					.map((el) => el.displayName)
					.join(', ')
					.slice(0, 20)}`
			: '';
	}

	getFullTitleFilter(filter: string): string {
		return this[filter].selected.length ? `${this[filter].selected.map((el) => el.displayName).join(', ')}` : '';
	}

	closeModal() {
		this.showModal = false;
	}

	openOrdersHistory(photographerId: number) {
		const url = this.#router.serializeUrl(this.#router.createUrlTree(['/company/photographer-orders', photographerId]));
		window.open(url, '_blank');
	}

	private searchTextSubscription() {
		this.topbarService.searchTextSubscribe.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe({
			next: (data) => {
				if (data !== this.photographers.searchText && !this.tmpPhotog) {
					this.photographers.searchText = data;

					// Gestione search chip e URL
					const params: Array<{ [key: string]: string }> = [{ search: data }];
					this.chips.find((el) => el.search) ? (this.chips.find((el) => el.search).search = data) : this.chips.unshift({ search: data });
					this.#filterService.addDataToUrl(params);

					setTimeout(() => {
						window.location.reload();
					}, 1000);
				}
			},
			error: (error) => console.error('Error in searchTextSubscription:', error)
		});
	}

	private queryParamsSubscribe() {
		this.#activatedRoute.queryParams.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((data) => {
			this.getData();
			this.getPhotographers(0, data);
		});
	}

	getBrandColumnValue(photographer: Photographer): string {
		let brandLabel = '';

		if (photographer.brands && photographer.brands?.length > 0) {
			const brands = photographer.brands.map((el) => {
				const categories = el.categories.map((cat) => cat.categoryName);
				return `${el.brandName} - ${categories.length ? categories.join(', ') : 'All Categories'}`;
			});

			brandLabel = brands.join(', ');
		}

		return brandLabel;
	}
}
